import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "../components/ui/button";
import { Plus, Eye, EyeOff, LayoutGrid, BarChart3, MessageSquare, List, Trash2, Search, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { saveLeads, getActionSuggestion, syncFromDB, PLAN_DATA } from "./demoConfig";
import NewLeadModal from "./NewLeadModal";
import PreDemoDrawer from "./PreDemoDrawer";
import DemoChoiceModal from "./DemoChoiceModal";
import "./demo2.css";
import CallIntelligence from "./CallIntelligence";
import AdminIntelPanel from "./AdminIntelPanel";
import { SourceBadge } from "./SourceBadge";
import CloserPerformance from "./CloserPerformance";
import WhatsAppChat from "./WhatsAppChat";
import GridView from "./GridView";
import { waGetUnread, waMarkRead, updateLeadField, deleteLeadAPI } from "./api";

// 4 visible kanban columns (simplified Feb 2026):
//   - "upcoming" absorbs demo_in_progress + no_show (rendered as flags on card, not separate columns)
//   - "demo_done" absorbs payment_sent (rendered as a flag on card)
//   - "won" is the renamed "converted" status (DB still uses "converted" — just label change)
//   - "cold" stays archived/collapsed
const COLUMNS = [
  { key: "new", label: "New", statuses: ["new"] },
  { key: "upcoming", label: "Upcoming", statuses: ["upcoming", "demo_in_progress", "no_show"] },
  { key: "demo_done", label: "Demo done — follow up", statuses: ["demo_done", "payment_sent"] },
  { key: "won", label: "Won", statuses: ["converted"] },
  { key: "cold", label: "Cold", statuses: ["cold"] },
];

const STATUS_FLAG = {
  demo_in_progress: { label: "On call now", color: "#f59e0b", bg: "#fef3c7" },
  no_show: { label: "No-show", color: "#dc2626", bg: "#fee2e2" },
  payment_sent: { label: "Payment link sent", color: "#0ea5e9", bg: "#e0f2fe" },
};

function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function CRM() {
  const { auth, live } = useOutletContext();
  const navigate = useNavigate();
  const leads = live?.leads || [];
  const [showCold, setShowCold] = useState(false);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [preDemoLead, setPreDemoLead] = useState(null);
  const [crmTab, setCrmTab] = useState("pipeline"); // "pipeline" | "grid" | "performance"
  const [waChatLead, setWaChatLead] = useState(null);
  const [unreadData, setUnreadData] = useState({ total: 0, perLead: {} });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [demoChoiceLead, setDemoChoiceLead] = useState(null);

  useEffect(() => {
    // Trigger DB seed once + initial unread fetch
    syncFromDB().catch(() => {});
    const loadUnread = () => waGetUnread().then(d => setUnreadData(d)).catch(() => {});
    loadUnread();
    const unreadInterval = setInterval(loadUnread, 30000);
    return () => clearInterval(unreadInterval);
  }, []);

  const refreshLeads = () => live?.refresh?.();

  const handleDeleteLead = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteLeadAPI(deleteTarget.id);
      setDeleteTarget(null);
      live?.refresh?.();
    } catch (e) {
      alert("Delete failed: " + (e?.message || "Unknown error"));
    } finally {
      setDeleting(false);
    }
  };

  const updateLead = async (id, updates) => {
    // Optimistic UI: also write through localStorage cache so any legacy reads stay consistent
    try {
      const cached = JSON.parse(localStorage.getItem("bja_leads") || "[]");
      const updated = cached.map((l) => (l.id === id ? { ...l, ...updates } : l));
      saveLeads(updated);
    } catch { /* ignore */ }
    await updateLeadField(id, updates);
    live?.refresh?.();
  };

  // Default status target when a lead is dropped into a column.
  // Picks the canonical status (first in the column's statuses array).
  const COLUMN_DEFAULT_STATUS = {
    new: "new",
    upcoming: "upcoming",
    demo_done: "demo_done",
    won: "converted",
    cold: "cold",
  };

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;
    const lead = leads.find((l) => l.id === draggableId);
    if (!lead) return;
    const targetStatus = COLUMN_DEFAULT_STATUS[destination.droppableId];
    if (!targetStatus) return;
    // Special case: dropping into "Won" → fire mark-paid with current convertedAmount or 0
    if (destination.droppableId === "won") {
      const amount = lead.paidAmount || lead.convertedAmount || lead.planAmount
        || (lead.selectedPlan && PLAN_DATA[lead.selectedPlan]?.priceNum)
        || (lead.recommendedPlan && PLAN_DATA[lead.recommendedPlan]?.priceNum)
        || 0;
      const ok = window.confirm(
        amount > 0
          ? `Mark ${lead.clientName} as Won at $${Number(amount).toLocaleString()}?\nThis will fire Meta CAPI Purchase.`
          : `Move ${lead.clientName} to Won? Amount is $0 — set plan/amount on Lead Detail first if you want CAPI to fire correctly.`
      );
      if (!ok) return;
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/demo/leads/${lead.id}/mark-paid`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ amount }),
        });
        if (!r.ok) throw new Error(`Mark-paid failed (${r.status})`);
        live?.refresh?.();
      } catch (e) {
        alert("Mark paid failed: " + (e?.message || "unknown"));
      }
      return;
    }
    // Standard column move — just update status
    await updateLead(lead.id, { status: targetStatus });
  };

  // Metrics
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const demosThisWeek = leads.filter((l) => l.demoDoneAt && new Date(l.demoDoneAt) >= weekAgo).length;
  const demosDone = leads.filter((l) => l.demoDoneAt);
  const converted = leads.filter((l) => l.status === "converted");
  const conversionRate = demosDone.length > 0 ? Math.round((converted.length / demosDone.length) * 100) : 0;
  const revenueThisWeek = leads
    .filter((l) => l.convertedAt && new Date(l.convertedAt) >= weekAgo)
    .reduce((sum, l) => sum + (l.convertedAmount || 0), 0);

  // Search filter — applied to BOTH pipeline & grid views
  const q = searchQuery.trim().toLowerCase();
  const visibleLeads = q
    ? leads.filter((l) => {
        const haystack = [
          l.clientName,
          l.targetRole,
          l.targetMarket,
          l.whatsappNumber,
          l.email,
          l.closerUsername,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
    : leads;

  return (
    <div className="crm-page" data-testid="crm-page">
      {/* Slim topbar — page header + key metrics + new lead */}
      <div className="crm-topbar crm-topbar-slim">
        <div className="crm-topbar-left">
          <h1 className="crm-title">Pipeline</h1>
          <span className="crm-subtitle" data-testid="crm-subtitle">
            <span className="crm-data-source">demo_leads</span> · {leads.length} total
          </span>
        </div>
        <div className="crm-topbar-center">
          <div className="crm-metric"><span className="crm-metric-value">{demosThisWeek}</span><span className="crm-metric-label">Demos / week</span></div>
          <div className="crm-metric"><span className="crm-metric-value">{conversionRate}%</span><span className="crm-metric-label">Close rate</span></div>
          <div className="crm-metric"><span className="crm-metric-value">${revenueThisWeek.toLocaleString()}</span><span className="crm-metric-label">Revenue / week</span></div>
        </div>
        <div className="crm-topbar-right">
          <Button size="sm" onClick={() => setNewLeadOpen(true)} data-testid="new-lead-btn" className="crm-new-lead-btn">
            <Plus className="w-4 h-4" /> New lead
          </Button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="crm-tabs" data-testid="crm-tabs">
        <button className={`crm-tab ${crmTab === "pipeline" ? "crm-tab-active" : ""}`} onClick={() => setCrmTab("pipeline")} data-testid="tab-pipeline">
          <LayoutGrid className="w-3.5 h-3.5" /> Pipeline
        </button>
        <button className={`crm-tab ${crmTab === "grid" ? "crm-tab-active" : ""}`} onClick={() => setCrmTab("grid")} data-testid="tab-grid">
          <List className="w-3.5 h-3.5" /> Grid
        </button>
        <button className={`crm-tab ${crmTab === "performance" ? "crm-tab-active" : ""}`} onClick={() => setCrmTab("performance")} data-testid="tab-performance">
          <BarChart3 className="w-3.5 h-3.5" /> My Performance
        </button>

        {crmTab !== "performance" && (
          <div className="crm-search-wrap" data-testid="crm-search-wrap">
            <Search className="crm-search-icon w-3.5 h-3.5" />
            <input
              type="text"
              className="crm-search-input"
              placeholder="Search by name, role, market, phone…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="crm-search-input"
            />
            {searchQuery && (
              <button
                className="crm-search-clear"
                onClick={() => setSearchQuery("")}
                data-testid="crm-search-clear"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {searchQuery && (
              <span className="crm-search-count" data-testid="crm-search-count">
                {visibleLeads.length} match{visibleLeads.length === 1 ? "" : "es"}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Admin intelligence panel (admin only) */}
      {auth.username === "admin" && (crmTab === "pipeline" || crmTab === "grid") && (
        <AdminIntelPanel leads={leads} />
      )}

      {crmTab === "performance" && (
        <CloserPerformance leads={leads} auth={auth} />
      )}

      {/* Grid view */}
      {crmTab === "grid" && (
        <GridView
          leads={visibleLeads}
          auth={auth}
          updateLead={updateLead}
          onPreDemo={setPreDemoLead}
          onWaChat={(lead) => setWaChatLead(waChatLead?.id === lead?.id ? null : lead)}
          waChatLead={waChatLead}
        />
      )}

      {/* Kanban board */}
      {crmTab === "pipeline" && (
      <DragDropContext onDragEnd={onDragEnd}>
      <div className="crm-board" data-testid="crm-board">
        {COLUMNS.map((col) => {
          if (col.key === "cold" && !showCold) {
            return (
              <div key={col.key} className="crm-column crm-column-cold-toggle">
                <button className="crm-show-cold" onClick={() => setShowCold(true)} data-testid="show-cold-btn">
                  <Eye className="w-4 h-4" /> Show cold
                </button>
              </div>
            );
          }
          const columnLeads = visibleLeads.filter((l) => col.statuses.includes(l.status));
          return (
            <Droppable droppableId={col.key} key={col.key}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`crm-column ${snapshot.isDraggingOver ? "crm-column-dragover" : ""}`}
                  data-testid={`column-${col.key}`}
                >
                  <div className="crm-column-header">
                    <span className="crm-column-title">{col.label}</span>
                    <span className="crm-column-count">{columnLeads.length}</span>
                    {col.key === "cold" && (
                      <button className="crm-hide-cold" onClick={() => setShowCold(false)}>
                        <EyeOff className="w-3 h-3" /> Hide
                      </button>
                    )}
                  </div>
                  <div className="crm-column-cards">
                    {columnLeads.map((lead, index) => (
                      <Draggable draggableId={lead.id} index={index} key={lead.id}>
                        {(dragProvided, dragSnapshot) => {
                          const action = getActionSuggestion(lead);
                          const planKey = lead.selectedPlan || lead.recommendedPlan;
                          const plan = planKey ? PLAN_DATA[planKey] : null;
                          const flag = STATUS_FLAG[lead.status];
                          const showPlan = col.key === "won" || col.key === "demo_done";
                          return (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              className={`crm-card crm-card-slim ${dragSnapshot.isDragging ? "crm-card-dragging" : ""}`}
                              data-testid={`lead-card-${lead.id}`}
                              style={dragProvided.draggableProps.style}
                            >
                      <div className="crm-card-row1">
                        <span className="crm-card-name crm-card-name-link" onClick={() => navigate(`/demo/lead/${lead.id}`)} data-testid={`lead-name-${lead.id}`}>
                          {lead.clientName} {lead.clientLastName || ""}
                        </span>
                        {flag && (
                          <span
                            className="crm-flag-pill"
                            style={{ color: flag.color, background: flag.bg }}
                            data-testid={`status-flag-${lead.status}`}
                          >{flag.label}</span>
                        )}
                      </div>
                      <div className="crm-card-row2">
                        {(lead.targetRole || "—")}{lead.targetMarket ? ` · ${lead.targetMarket}` : ""}
                      </div>
                      <div className="crm-card-row3">
                        <span>{formatDate(lead.scheduledAt) || "Not scheduled"}</span>
                        <span>·</span>
                        <span>{lead.closerName || "Unassigned"}</span>
                        {showPlan && plan && (<><span>·</span><span className="crm-card-plan-inline">{plan.price}</span></>)}
                      </div>
                      {action.text && (col.key === "demo_done" || col.key === "new") && (
                        <div className={`crm-action-mini badge-${action.color}`} data-testid="action-badge">
                          {action.text}
                        </div>
                      )}
                      <div className="crm-card-actions">
                        {(lead.status === "converted" || lead.paymentStatus === "paid") ? (
                          <span data-testid={`paid-chip-${lead.id}`} className="crm-paid-chip">
                            ✓ Paid {lead.paidAmount || lead.convertedAmount ? `· $${Number(lead.paidAmount || lead.convertedAmount).toLocaleString()}` : ""}
                          </span>
                        ) : (
                          <Button size="sm" variant="outline" className="crm-open-btn" onClick={() => navigate(`/demo/lead/${lead.id}`)} data-testid={`open-lead-${lead.id}`}>
                            Open
                          </Button>
                        )}
                        {lead.whatsappNumber && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const opening = waChatLead?.id !== lead.id;
                              setWaChatLead(opening ? lead : null);
                              if (opening && unreadData.perLead[lead.id]) {
                                waMarkRead(lead.id).then(() => waGetUnread().then(d => setUnreadData(d)));
                              }
                            }}
                            className={`crm-wa-btn ${waChatLead?.id === lead.id ? "crm-wa-btn-active" : ""}`}
                            data-testid={`wa-btn-${lead.id}`}
                            title="Inline WhatsApp chat"
                          >
                            <MessageSquare className="w-3 h-3" />
                            {unreadData.perLead[lead.id] > 0 && (
                              <span className="crm-wa-card-unread" data-testid={`wa-unread-${lead.id}`}>{unreadData.perLead[lead.id]}</span>
                            )}
                          </Button>
                        )}
                        {lead.status !== "converted" && lead.paymentStatus !== "paid" && col.key !== "won" && (
                          <Button size="sm" className="crm-start-demo-btn" onClick={() => setDemoChoiceLead(lead)} data-testid={`start-demo-btn-${lead.id}`}>
                            Demo
                          </Button>
                        )}
                        <MoreMenu lead={lead} updateLead={updateLead} onDelete={() => setDeleteTarget(lead)} />
                      </div>
                      {waChatLead?.id === lead.id && (
                        <WhatsAppChat lead={lead} onClose={() => setWaChatLead(null)} />
                      )}
                            </div>
                          );
                        }}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {columnLeads.length === 0 && (
                      <div className="crm-empty">No leads</div>
                    )}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
      </DragDropContext>
      )}

      {/* Modals / Drawers */}
      <NewLeadModal
        open={newLeadOpen}
        onClose={() => setNewLeadOpen(false)}
        auth={auth}
        onSave={refreshLeads}
      />
      {preDemoLead && (
        <PreDemoDrawer
          lead={preDemoLead}
          auth={auth}
          onClose={() => { setPreDemoLead(null); refreshLeads(); }}
          onStartDemo={() => {
            const targetLead = preDemoLead;
            setPreDemoLead(null);
            setDemoChoiceLead(targetLead);
          }}
        />
      )}

      {demoChoiceLead && (
        <DemoChoiceModal
          lead={demoChoiceLead}
          onClose={() => setDemoChoiceLead(null)}
        />
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-lead-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.clientName} {deleteTarget?.clientLastName || ""}</strong>
              {deleteTarget?.whatsappNumber && <span> ({deleteTarget.whatsappNumber})</span>}
              <br /><br />
              This permanently removes the lead, their entire WhatsApp chat history, and all reminders.
              <br /><strong>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-lead-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteLead(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-lead-confirm"
            >
              {deleting ? "Deleting…" : "Yes, delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MoreMenu({ lead, updateLead, onDelete }) {
  const [open, setOpen] = useState(false);

  // Status transitions — every status gets a proper "what's next" menu so
  // closers can recover leads without dragging (works on mobile + faster).
  const actions = [];
  const markUpcoming = () => updateLead(lead.id, { status: "upcoming" });
  const markDemoDone = () => updateLead(lead.id, { status: "demo_done", demoDoneAt: new Date().toISOString() });
  const markCold = () => updateLead(lead.id, { status: "cold" });
  const markNoShow = () => updateLead(lead.id, { status: "no_show" });

  if (lead.status === "new") {
    actions.push({ label: "Mark as booked → Upcoming", action: markUpcoming });
    actions.push({ label: "Mark cold", action: markCold });
  }
  if (lead.status === "upcoming") {
    actions.push({ label: "Mark demo done", action: markDemoDone });
    actions.push({ label: "Mark no-show", action: markNoShow });
    actions.push({ label: "Cancel demo", action: markCold });
  }
  if (lead.status === "no_show") {
    actions.push({ label: "Rebooked → Upcoming", action: markUpcoming });
    actions.push({ label: "Demo eventually done", action: markDemoDone });
    actions.push({ label: "Mark cold", action: markCold });
  }
  if (lead.status === "demo_in_progress") {
    actions.push({ label: "Mark demo done", action: markDemoDone });
    actions.push({ label: "Mark no-show", action: markNoShow });
  }
  if (lead.status === "demo_done") {
    actions.push({ label: "Mark payment sent", action: () => updateLead(lead.id, { status: "payment_sent", paymentSentAt: new Date().toISOString() }) });
    actions.push({ label: "Mark cold", action: markCold });
  }
  if (lead.status === "payment_sent") {
    actions.push({ label: "Mark converted (won)", action: () => {
      const plan = lead.recommendedPlan ? PLAN_DATA[lead.recommendedPlan] : null;
      updateLead(lead.id, { status: "converted", convertedAt: new Date().toISOString(), convertedAmount: plan?.priceNum || 0 });
    }});
    actions.push({ label: "Back to demo done", action: markDemoDone });
    actions.push({ label: "Mark cold", action: markCold });
  }
  if (lead.status === "converted") {
    actions.push({ label: "Reopen → demo done", action: markDemoDone });
    actions.push({ label: "Refund → cold", action: markCold });
  }
  if (lead.status === "cold") {
    actions.push({ label: "Reopen → demo done", action: markDemoDone });
    actions.push({ label: "Rebook → Upcoming", action: markUpcoming });
  }

  return (
    <div className="crm-more-menu" style={{ position: "relative" }}>
      <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid={`more-btn-${lead.id}`}>
        ...
      </Button>
      {open && (
        <div className="crm-dropdown" onMouseLeave={() => setOpen(false)}>
          {actions.map((a, i) => (
            <button key={i} className="crm-dropdown-item" onClick={() => { a.action(); setOpen(false); }} data-testid={`more-action-${lead.id}-${i}`}>
              {a.label}
            </button>
          ))}
          {actions.length > 0 && <div className="crm-dropdown-sep" />}
          <button
            className="crm-dropdown-item crm-dropdown-item-danger"
            onClick={() => { setOpen(false); onDelete && onDelete(); }}
            data-testid={`delete-lead-btn-${lead.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete lead
          </button>
        </div>
      )}
    </div>
  );
}
